import { unregister } from 'serviceWorker';
import { api, refreshPermissions } from 'utils/API';
import { LoggingService } from './logging/LoggingService';
import ProfilePageService from './ProfileService';
import StationsService from './StationsService';
import { deleteWebPushSubscription, firebaseSignOut } from 'services/Firebase';
import LocalStorageService from 'services/LocalStorageService';

const getAuthHeaders = () => {
  const userData = LocalStorageService.getUserData();
  const token = userData?.accessToken;
  return token
    ? {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }
    : {
        'Content-Type': 'application/json',
      };
};

const handleJsonError = async (res, fallbackMessage) => {
  let body = null;
  try {
    body = await res.json();
  } catch (_) {}

  const error = new Error(body?.message || fallbackMessage || 'Request failed');
  error.status = res.status;
  if (body?.status) error.code = body.status;
  error.body = body;
  throw error;
};

const Auth = {
  isAuthenticated() {
    const userData = LocalStorageService.getUserData();
    return !!(userData && userData.user);
  },

  changePassword(option, loginData) {
    return fetch(`${api.baseURL}/${option.url}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...option.headers,
      },
      body: JSON.stringify(loginData),
    })
      .then(async (res) => {
        const isError = !res.ok;
        let responseBody;

        try {
          responseBody = await res.json();
        } catch (e) {
          responseBody = null;
        }

        if (isError) {
          return Promise.reject({
            status: res.status,
            statusText: res.statusText,
            body: responseBody,
          });
        }

        return {
          status: res.status,
          body: responseBody,
          isError: false,
        };
      })
      .catch((error) => {
        console.error('changePassword error', error);
        throw error;
      });
  },

  authenticate(loginData, successCb, errorCb) {
    fetch(api['auth'], {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginData),
    })
      .then((res) => {
        if (res.status === 401 && res.headers.get('password-expired-token')) {
          successCb({
            expiredToken: res.headers.get('password-expired-token'),
          });
        }
        if (!res.ok) {
          errorCb();
        } else {
          return res;
        }
      })
      .then((res) => res.json())
      .then(async (userData) => {
        LocalStorageService.setUserData(userData);

        const stations = await StationsService.getStations()
          .then(({ data }) => data || null)
          .catch((err) => console.log('getStations', err));

        const userPhoto = await ProfilePageService.getProfileImage()
          .then((res) => (res.data && res.data.base64Value) || null)
          .catch((err) => console.log('getProfileImage', err));

        await ProfilePageService.getProfileData().then(async (res) => {
          if (res?.data && stations) {
            const warningMessageStationList = stations.reduce((acc, item) => {
              if (item.showWarningMessage) acc.push(item.stationCode);
              return acc;
            }, []);

            const defaultStation = await StationsService.getProfileStation(
              stations,
              res.data.defaultStationId
            );

            userData.user.email = userData.upn;
            userData.user.userPhoto = userPhoto;
            userData.station = defaultStation.stationCode;
            userData.manualServiceRegistrationComplete =
              defaultStation.manualServiceRegistrationComplete;
            userData.rtcConfigurationEnabled =
              defaultStation.rtcConfigurationEnabled;
            userData.localTimeZone = defaultStation.timezone;
            userData.warningMessageStationList = warningMessageStationList;
            userData.countryIsoAlpha2 = defaultStation.countryIsoAlpha2;

            LocalStorageService.setUserData(userData);
          }
        });

        await refreshPermissions();
        LoggingService.sendLogs('SUCCESSFUL_LOGIN');

        successCb();
      })
      .catch((err) => console.log('authenticate error', err));
  },

  signout(cb) {
    firebaseSignOut();
    unregister();

    const logoutPending = sessionStorage.getItem('logout-reload-pending');
    const token = LocalStorageService.getWebPushSubscription();
    const appLanguage = localStorage.getItem('appLanguage');
    const versionFileToken = LocalStorageService.getVersionFileToken();

    const restorePersistedStorage = () => {
      if (logoutPending) {
        sessionStorage.setItem('logout-reload-pending', 'true');
      }
      if (appLanguage) {
        localStorage.setItem('appLanguage', appLanguage);
      }
      if (versionFileToken) {
        LocalStorageService.setVersionFileToken(versionFileToken);
      }
    };

    const cleanupPromises = [
      deleteWebPushSubscription(token)
        .then(() => {
          localStorage.clear();
          sessionStorage.clear();
          restorePersistedStorage();
        })
        .catch(() => {
          localStorage.clear();
          sessionStorage.clear();
          restorePersistedStorage();
          LocalStorageService.setWebPushSubscription(token);
        }),
    ];
    if ('caches' in window) {
      cleanupPromises.push(
        caches
          .keys()
          .then((keys) => {
            return Promise.all(keys.map((key) => caches.delete(key)));
          })
          .catch((err) => {
            console.warn('Cache clearing failed, proceeding with logout:', err);
          })
      );
    }

    // Wait for all cleanup operations to complete before calling callback
    Promise.all(cleanupPromises).then(() => {
      cb();
    });
  },
  verify: async (token) => {
    const response = await fetch(
      `${api.baseURL}/profile/recovery/email/verify?token=${encodeURIComponent(token)}`
    );
    if (!response.ok) throw new Error('Verification failed');
    return response.json();
  },

  async requestPasswordReset({ adAccount, recoveryEmail }) {
    const res = await fetch(`${api.baseURL}/profile/password/reset/init`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ adAccount, recoveryEmail }),
    });

    if (!res.ok) {
      await handleJsonError(res, 'Init failed');
    }

    return res.json();
  },

  async verifyCode({ adAccount, recoveryEmail, passwordResetCode }) {
    const res = await fetch(`${api.baseURL}/profile/password/reset/verify`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        adAccount,
        recoveryEmail,
        passwordResetCode,
      }),
    });

    if (!res.ok) {
      await handleJsonError(res, 'Verify failed');
    }

    return res.json();
  },

  async resetPassword({
    adAccount,
    recoveryEmail,
    passwordResetCode,
    password,
  }) {
    const res = await fetch(`${api.baseURL}/profile/password/reset`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        adAccount,
        recoveryEmail,
        passwordResetCode,
        password,
      }),
    });

    if (!res.ok) {
      await handleJsonError(res, 'Reset failed');
    }

    return res.json();
  },
};

export default Auth;
